import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import apiClient from "../helpers/apiClient";
import { useUser } from "./UserContext/UserContext";
import {
    ONBOARDING_STEP_KEYS,
    ONBOARDING_STEP_KEY_TO_INDEX,
    HIDDEN_ONBOARDING_STEP_KEYS,
    resolveOnboardingStepKey,
    isOnboardingUrlStepResolved,
} from "../features/onboarding/components/onboardingStepConfig";

const OnboardingContext = createContext();

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error("useOnboarding must be used within an OnboardingProvider");
    }
    return context;
};

const LAST_STEP_INDEX = ONBOARDING_STEP_KEYS.length - 1;

/**
 * Wizard step key → the key the backend schema actually stores.
 *
 * The wizard calls its vehicle step "add-car", but `onboardingSteps.stepStatus` defines it as
 * `cars`. Mongoose strips unknown keys from that typed subdocument, so writes under "add-car"
 * were silently discarded and the step could never be marked complete.
 */
const STEP_KEY_TO_BACKEND_KEY = {
    "add-car": "cars",
};

const toBackendStepKey = (stepKey) => STEP_KEY_TO_BACKEND_KEY[stepKey] ?? stepKey;

const normalizeStepStatus = (savedStatus = {}) => {
    const legacyListVehicleComplete =
        savedStatus.listVehicle ||
        (savedStatus.locations && savedStatus.cars);

    return {
        companyInfo: !!savedStatus.companyInfo,
        locations: !!(savedStatus.locations || legacyListVehicleComplete),
        categories: !!(savedStatus.categories || savedStatus.cars),
        "add-car": !!(savedStatus["add-car"] || savedStatus.cars || legacyListVehicleComplete),
        stripe: !!savedStatus.stripe,
        insurance: !!savedStatus.insurance,
    };
};

const readStepKeyFromUrl = (search) => {
    const params = new URLSearchParams(search);
    return resolveOnboardingStepKey(params.get("step"));
};

export const OnboardingProvider = ({ children }) => {
    const { state: userState, fetchCompanyData } = useUser();
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [stepStatus, setStepStatus] = useState({
        companyInfo: false,
        locations: false,
        categories: false,
        "add-car": false,
        stripe: false,
        insurance: false,
    });

    const [currentStepKey, setCurrentStepKey] = useState(() => readStepKeyFromUrl(location.search));
    const [isStepResolved, setIsStepResolved] = useState(() =>
        isOnboardingUrlStepResolved(new URLSearchParams(location.search).get("step")),
    );

    const currentStep = ONBOARDING_STEP_KEY_TO_INDEX[currentStepKey] ?? 0;

    useLayoutEffect(() => {
        const params = new URLSearchParams(location.search);
        const stepParam = params.get("step");
        const hasStripeReturn =
            params.has("stripe-onboard-connection-success") || params.has("stripe-onboard-connection-failed");
        const resolvedKey = hasStripeReturn ? "stripe" : resolveOnboardingStepKey(stepParam);

        if (!stepParam || stepParam !== resolvedKey) {
            setIsStepResolved(false);
            params.set("step", resolvedKey);
            history.replace(`/onboarding?${params.toString()}`);
            return;
        }

        setCurrentStepKey(resolvedKey);
        setIsStepResolved(true);
    }, [history, location.search]);

    useEffect(() => {
        if (userState?.user?.company?.onboardingSteps) {
            const { stepStatus: savedStatus } = userState.user.company.onboardingSteps;
            if (savedStatus) {
                setStepStatus(normalizeStepStatus(savedStatus));
            }
        }
    }, [userState]);

    const updateStepProgress = useCallback(async (stepKey, stepIndex, completed = true) => {
        try {
            const response = await apiClient.patch("/api/company/myCompany", {
                onboardingSteps: {
                    currentStep: stepIndex,
                    stepStatus: {
                        [toBackendStepKey(stepKey)]: completed,
                    },
                },
            });

            if (response?.data?.success) {
                setStepStatus((prev) => ({
                    ...prev,
                    [stepKey]: completed,
                }));
                return true;
            }
            return false;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error updating step progress:", error);
            return false;
        }
    }, []);

    const completeOnboarding = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.patch("/api/company/myCompany", {
                onboardingSteps: {
                    completed: true,
                    currentStep: LAST_STEP_INDEX,
                },
            });

            if (response?.data?.success) {
                if (fetchCompanyData) {
                    await fetchCompanyData();
                }
                return { success: true };
            }
            const errorMsg = response?.data?.message || "Failed to complete onboarding";
            return { success: false, error: errorMsg };
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error completing onboarding:", error);
            const errorMsg = error?.response?.data?.message || error?.message || "Failed to complete onboarding";
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    }, [fetchCompanyData]);

    const goToNextStep = useCallback(() => {
        let nextIndex = currentStep + 1;
        while (nextIndex <= LAST_STEP_INDEX && HIDDEN_ONBOARDING_STEP_KEYS.includes(ONBOARDING_STEP_KEYS[nextIndex])) {
            nextIndex += 1;
        }
        if (nextIndex > LAST_STEP_INDEX || HIDDEN_ONBOARDING_STEP_KEYS.includes(ONBOARDING_STEP_KEYS[nextIndex])) {
            return;
        }
        history.push(`/onboarding?step=${ONBOARDING_STEP_KEYS[nextIndex]}`);
    }, [currentStep, history]);

    const goToPreviousStep = useCallback(() => {
        let prevIndex = currentStep - 1;
        while (prevIndex >= 0 && HIDDEN_ONBOARDING_STEP_KEYS.includes(ONBOARDING_STEP_KEYS[prevIndex])) {
            prevIndex -= 1;
        }
        if (prevIndex < 0) {
            return;
        }
        history.push(`/onboarding?step=${ONBOARDING_STEP_KEYS[prevIndex]}`);
    }, [currentStep, history]);

    const value = {
        currentStep,
        currentStepKey,
        isStepResolved,
        stepStatus,
        loading,
        goToNextStep,
        goToPreviousStep,
        updateStepProgress,
        completeOnboarding,
        STEP_KEY_TO_INDEX: ONBOARDING_STEP_KEY_TO_INDEX,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

OnboardingProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
